import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import {
	type ColorScheme,
	parseColorScheme,
	persistCardScheme,
	readStoredCardScheme,
} from "@/app/scheme";
import {
	type ColorTheme,
	canvasBgOf,
	parseColorTheme,
	persistCardTheme,
	readStoredCardTheme,
} from "@/app/theme";
import { publicApi } from "@/shared/api/client";

type CardSchemeContextValue = {
	scheme: ColorScheme;
	theme: ColorTheme;
};

const CardSchemeContext = createContext<CardSchemeContextValue | null>(null);

type SeasonPayload = {
	cardColorScheme?: string;
	cardColorTheme?: string;
};

async function fetchAdminCardAppearance(): Promise<{
	scheme: ColorScheme;
	theme: ColorTheme;
} | null> {
	try {
		const payload = await publicApi.get("season").json<SeasonPayload>();
		if (!payload.cardColorScheme && !payload.cardColorTheme) {
			return null;
		}
		return {
			scheme: parseColorScheme(payload.cardColorScheme),
			theme: parseColorTheme(payload.cardColorTheme),
		};
	} catch {
		return null;
	}
}

export function CardSchemeProvider({ children }: { children: ReactNode }) {
	const [scheme, setScheme] = useState<ColorScheme>(() =>
		readStoredCardScheme(),
	);
	const [theme, setTheme] = useState<ColorTheme>(() => readStoredCardTheme());

	useEffect(() => {
		const pull = () => {
			void fetchAdminCardAppearance().then((fromAdmin) => {
				if (!fromAdmin) {
					return;
				}
				setScheme(fromAdmin.scheme);
				setTheme(fromAdmin.theme);
				persistCardScheme(fromAdmin.scheme);
				persistCardTheme(fromAdmin.theme);
			});
		};

		pull();
		window.addEventListener("focus", pull);
		return () => window.removeEventListener("focus", pull);
	}, []);

	useEffect(() => {
		const root = document.documentElement;
		root.dataset.scheme = scheme;
		root.dataset.theme = theme;
		root.style.setProperty("--card-device-bg", canvasBgOf(theme, scheme));
		root.style.setProperty("--play-canvas-bg", canvasBgOf(theme, "dark"));
		root.style.setProperty(
			"--card-row-border",
			scheme === "dark" ? "#2c313a" : "#e8ecf1",
		);
		root.style.setProperty(
			"--card-device-border",
			scheme === "dark" ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.1)",
		);
	}, [scheme, theme]);

	const value = useMemo<CardSchemeContextValue>(
		() => ({ scheme, theme }),
		[scheme, theme],
	);

	return (
		<CardSchemeContext.Provider value={value}>
			{children}
		</CardSchemeContext.Provider>
	);
}

export function useCardScheme(): CardSchemeContextValue {
	const value = useContext(CardSchemeContext);
	if (!value) {
		throw new Error("useCardScheme must be used inside CardSchemeProvider");
	}
	return value;
}
